import { Avatar, Badge, Button, Dropdown, Input, Space, Tag, Tooltip, Typography, theme } from 'antd';
import { Bell, Search } from 'lucide-react';
import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth } from '../../stores/AuthContext';
import ExchangeRateWidget from '../ExchangeRateWidget';
import { ACQ_CLIENT_VERSION, ACQ_CLIENT_RELEASED_AT } from '../../version';
import { useServerVersion } from '@hooks/useServerVersion';

interface TopBarProps {
  acqOnSearchClick: () => void;
}

/**
 * Верхня смужка: лого + глобальний пошук + дзвоник + аватар.
 * Висота 46 (узгоджено з ТЗ редизайну). Пошук відкриває CommandPalette
 * через Ctrl+K або клік (acqOnSearchClick).
 */
export default function TopBar({ acqOnSearchClick }: TopBarProps) {
  const { user, logout } = useAuth();
  const { token: acqTheme } = theme.useToken();
  const acqServerVersion = useServerVersion();
  const acqServer = acqServerVersion.data?.version ?? null;
  const acqMatch = acqServer === null || acqServer === ACQ_CLIENT_VERSION;
  const acqVersionTooltip = acqServer
    ? `Клієнт: v${ACQ_CLIENT_VERSION} (${ACQ_CLIENT_RELEASED_AT})\nСервер: v${acqServer}${acqMatch ? '' : '\n⚠ Версії не збігаються'}`
    : `Клієнт: v${ACQ_CLIENT_VERSION} (${ACQ_CLIENT_RELEASED_AT})\nСервер: недоступний`;

  const acqUserMenu: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: 'Профіль' },
    { type: 'divider' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Вийти', danger: true },
  ];

  const acqInitials =
    `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}` || 'A';

  return (
    <div
      style={{
        height: 46,
        background: '#fff',
        borderBottom: `1px solid ${acqTheme.colorBorderSecondary}`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 14px',
        gap: 14,
      }}
    >
      {/* Лого */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            background: '#534AB7',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 500,
            fontSize: 13,
          }}
        >
          К
        </div>
        <Typography.Text strong style={{ fontSize: 14 }}>Квантум</Typography.Text>
        <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{acqVersionTooltip}</span>}>
          <Tag
            color={acqMatch ? 'default' : 'warning'}
            style={{ marginLeft: 4, fontSize: 11, cursor: 'help' }}
          >
            v{ACQ_CLIENT_VERSION}
            {acqServer && !acqMatch && ` ↔ v${acqServer}`}
          </Tag>
        </Tooltip>
      </div>

      {/* Search (клік відкриває CommandPalette) */}
      <div style={{ flex: 1, maxWidth: 360, marginLeft: 'auto' }}>
        <Input
          placeholder="Пошук документа, контрагента, товару… (Ctrl+K)"
          prefix={<Search size={14} />}
          onClick={acqOnSearchClick}
          readOnly
          style={{ cursor: 'pointer' }}
        />
      </div>

      {/* Курси НБУ */}
      <ExchangeRateWidget />

      {/* Дзвоник зі індикатором */}
      <Badge dot offset={[-4, 4]} color="#E24B4A">
        <Button type="text" icon={<Bell size={16} />} />
      </Badge>

      {/* Аватар */}
      <Dropdown
        menu={{
          items: acqUserMenu,
          onClick: ({ key }) => { if (key === 'logout') logout(); },
        }}
        placement="bottomRight"
      >
        <Space size={8} style={{ cursor: 'pointer' }}>
          <Avatar
            size={30}
            style={{ background: '#1D9E75', color: '#fff', fontWeight: 500, fontSize: 12 }}
          >
            {acqInitials}
          </Avatar>
        </Space>
      </Dropdown>
    </div>
  );
}
